import { InputTypes, InputWidthValues, InputEvents } from '../types';
import { GzInputWebComponent } from './GzInputWebComponent';
import { html, PropertyValues, TemplateResult, unsafeCSS } from 'lit';
import { property, state, customElement } from 'lit/decorators.js';
import { isElementOverflowed, OverflowDirection } from '../../../utils/htmlElement.utils';
import { GzTooltip } from '../../gzTooltip';
import { classMap } from 'lit/directives/class-map.js';
import { styles as textInputStyles } from '../view/input-text.scss';

@customElement('gz-input')
export class GzClearableInputWebComponent extends GzInputWebComponent {
  static get styles() {
    return [...GzInputWebComponent.styles, unsafeCSS(`${textInputStyles}`)];
  }

  /**
   * The default type is text
   */
  type = InputTypes.TEXT;

  value = '';

  /**
   * The default input size is full-width - will stretch as wide as its parent
   */
  inputWidth = InputWidthValues.FULL;

  /**
   * The canDelete property controls the CLEAR (X) button visibility in the input
   */
  @state()
  protected canDelete = false;

  /**
   * Default input max length is 524288;
   */
  @property({ type: Number, attribute: true, reflect: true })
  maxLength = 524288;

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
    this.setCanDelete(this.value?.length > 0);
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(InputEvents.CHANGE, this.handleInputChange);
  }

  disconnectedCallback() {
    this.removeEventListener(InputEvents.CHANGE, this.handleInputChange);
    super.disconnectedCallback();
  }

  updated(changedProperties: PropertyValues) {
    this.keyCounter();
  }

  render() {
    const classes = {
      input: true,
      invalid: this.invalid,
      'floating-label': this.inputElement?.value,
      'has-label': this.label,
    };

    return html`
      <div
        @mouseenter="${e => this.onInputMouseEnter(e, OverflowDirection.HORIZONTAL)}"
        @mousemove="${GzTooltip.align}"
        @mouseleave="${GzTooltip.hide}"
        class="${classMap(classes)}"
        part="input"
      >
        ${this.renderInputLabel()}
        <input
          class="input--field"
          type="${this.type}"
          ?disabled="${this.disabled}"
          ?readonly="${this.readonly}"
          aria-label="${this.label}"
          .value="${this.value}"
          .placeholder="${this.placeholder}"
          @keyup="${this.onKeyUp}"
          @keydown="${() => this.setActive(true)}"
          @change="${this.onUserInput}"
          @blur="${this.onInputBlur}"
          @input="${this.handleInputEvent}"
          maxlength="${this.hasCounter ? this.maxChars : this.maxLength}"
          part="input-field"
        />
        ${this.renderInvalidIcon()} ${this.renderSuccessIcon()} ${this.renderInputElements()}
      </div>
      ${this.renderCounter()}
    `;
  }

  protected setCanDelete(value: boolean) {
    this.canDelete = value;
    this.requestUpdate();
  }

  protected renderInputElements(): TemplateResult | undefined {
    if (!this.canDelete) {
      return;
    }
    return this.renderClearButton();
  }

  protected onKeyUp(event: KeyboardEvent) {
    super.onKeyUp(event);
    this.keyCounter();
  }

  protected onUserInput() {
    this.setCanDelete(this.inputElement.value.length > 0);
    super.onUserInput();
  }

  public clearInputValue(e: Event) {
    e.preventDefault();
    /**
     * Clear the input value and emit the change event
     */
    this.setInputElementValue('');
    this.inputElement.focus();
    this.emitInputEvent(InputEvents.CHANGE);
    this.setCanDelete(false);
  }

  public focusInput() {
    this.inputElement.focus();
  }

  private onInputMouseEnter(event: MouseEvent, overflowDirection = OverflowDirection.ANY): void {
    if (!this.invalid) {
      GzTooltip.hide();
    }

    if (this.getAttribute('type') === InputTypes.PASSWORD) {
      return;
    }
    if (
      isElementOverflowed(this.inputElement, overflowDirection) ||
      isElementOverflowed(this.inputLabelRef, overflowDirection)
    ) {
      GzTooltip.show(this.value, event);
    }
  }
}
